/**
 * Signup wizard — three public steps, then tenant creation.
 *
 *   1. program identity (team name + team code, live availability check)
 *   2. Team Admin account (email, name, password) + trial callout
 *   3. DPA / ToS click-through + FERPA acknowledgement  → POST /signup/v1
 *
 * The current step lives in the URL (`/signup?step=2`) and the non-secret draft
 * in sessionStorage, so a refresh mid-signup resumes where the user was.
 * Passwords are never persisted: a refresh past step 2 returns there.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import type { SignupOptions, SignupResult } from '../../lib/api-client';
import { ApiError } from '../../lib/api-error';
import { CONSENT_DOCUMENT_VERSIONS, PRODUCT_NAME, ROUTES } from '../../lib/constants';

import { DpaFerpaScreen } from './dpa-ferpa-screen';
import { OwnerStep, TeamStep } from './signup-steps';
import { SignupSuccess } from './signup-success';
import {
  PASSWORD_REQUIREMENT_HINTS,
  SERVER_ERROR_FIELDS,
  validateEmail,
  validatePassword,
  validateSlug,
  validateTeamName,
} from './signup-validation';
import { useSignupDraft, useSlugAvailability, type SignupDraft } from './use-signup-draft';

const STEP_LABELS = ['Program', 'Account', 'Consent'] as const;

type FieldErrors = Partial<
  Record<'teamName' | 'slug' | 'email' | 'displayName' | 'password' | 'confirmPassword', string>
>;

export function SignupWizard(): JSX.Element {
  const apiClient = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { draft, patchDraft, clearDraft } = useSignupDraft();
  const slugCheck = useSlugAvailability(apiClient, draft.slug);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [options, setOptions] = useState<SignupOptions | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<SignupResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const hasPassword = password !== '';

  const requestedStep = Number(searchParams.get('step') ?? '1');
  const step = Number.isFinite(requestedStep) ? Math.min(3, Math.max(1, requestedStep)) : 1;

  const goToStep = useCallback(
    (next: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('step', String(next));
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  // Consent versions + the live password/slug policy.
  useEffect(() => {
    const controller = new AbortController();
    apiClient
      .getSignupOptions(controller.signal)
      .then(setOptions)
      .catch(() => setOptions(null));
    return () => controller.abort();
  }, [apiClient]);

  // A refresh past step 2 cannot restore the password.
  useEffect(() => {
    if (step === 3 && !hasPassword && result === null) {
      setNotice('Re-enter your password to continue — it is never saved in the browser.');
      goToStep(2);
    }
  }, [goToStep, hasPassword, result, step]);

  function submitTeamStep(): void {
    const found: FieldErrors = {};
    const teamNameError = validateTeamName(draft.teamName);
    const slugError = validateSlug(draft.slug);
    if (teamNameError) found.teamName = teamNameError;
    if (slugError) found.slug = slugError;
    if (slugCheck.status === 'unavailable' && slugCheck.slug === draft.slug) {
      found.slug = 'That team code is already taken. Try another.';
    }
    setErrors(found);
    if (Object.keys(found).length === 0) goToStep(2);
  }

  function submitOwnerStep(): void {
    const found: FieldErrors = {};
    const emailError = validateEmail(draft.email, 'staff');
    const passwordError = validatePassword(password);
    if (emailError) found.email = emailError;
    if (passwordError) found.password = passwordError;
    if (confirmPassword !== password) found.confirmPassword = 'Passwords do not match.';
    setErrors(found);
    setNotice(null);
    if (Object.keys(found).length === 0) goToStep(3);
  }

  async function createTeam(): Promise<void> {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const created = await apiClient.createTeam({
        team_name: draft.teamName.trim(),
        tenant_slug: draft.slug,
        owner_email: draft.email.trim(),
        owner_password: password,
        ...(draft.displayName.trim() === ''
          ? {}
          : { owner_display_name: draft.displayName.trim() }),
        ferpa_acknowledgement: true,
        dpa_acceptance: true,
      });
      clearDraft();
      setPassword('');
      setConfirmPassword('');
      setResult(created);
    } catch (error) {
      const apiError = ApiError.isApiError(error)
        ? error
        : new ApiError({ status: 0, code: 'UNKNOWN', message: 'Signup failed. Please try again.' });
      const field = SERVER_ERROR_FIELDS[apiError.code];
      setSubmitError(apiError.message);
      if (field !== undefined) {
        setErrors({ [field]: apiError.message });
        goToStep(field === 'teamName' || field === 'slug' ? 1 : 2);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const dpaVersion = options?.consent.dpa_version ?? CONSENT_DOCUMENT_VERSIONS.dpa;

  return (
    <div className="auth">
      <Link to={ROUTES.landing} className="auth__brand">
        <img src="/images/logo-mark.png" alt="" width={32} height={32} />
        <span>
          GRIDIRON <em>TRACKER</em>
        </span>
      </Link>
      <main className="auth__panel auth__panel--wide">
        {/* Every page owes screen readers exactly one top-level heading. */}
        <h1 className="auth__title">
          {result === null ? `Start your team on ${PRODUCT_NAME}` : `${result.name} is ready`}
        </h1>
        {result === null ? (
          <>
            <ol className="wizard__steps" aria-label={`${PRODUCT_NAME} signup progress`}>
              {STEP_LABELS.map((label, index) => {
                const position = index + 1;
                const state = position === step ? 'current' : position < step ? 'done' : 'upcoming';
                return (
                  <li className="wizard__step" key={label} data-state={state}>
                    <span className="wizard__step-bar" />
                    <span>
                      {position}. {label}
                    </span>
                  </li>
                );
              })}
            </ol>

            {options?.is_signup_enabled === false ? (
              <div className="banner banner--warning" role="status">
                Self-service signup is paused right now. Contact sales and we will create your
                portal.
              </div>
            ) : null}
            {notice ? (
              <div className="banner banner--warning" role="status">
                {notice}
              </div>
            ) : null}

            {step === 1 ? (
              <TeamStep
                values={{ teamName: draft.teamName, slug: draft.slug }}
                errors={errors}
                slugCheck={slugCheck}
                onChange={patchDraft}
                onSubmit={submitTeamStep}
              />
            ) : null}

            {step === 2 ? (
              <OwnerStep
                values={{
                  email: draft.email,
                  displayName: draft.displayName,
                  password,
                  confirmPassword,
                }}
                errors={errors}
                passwordRequirements={
                  options?.password_policy.requirements ?? PASSWORD_REQUIREMENT_HINTS
                }
                onChange={(patch) => {
                  if (patch.password !== undefined) setPassword(patch.password);
                  if (patch.confirmPassword !== undefined)
                    setConfirmPassword(patch.confirmPassword);
                  const draftPatch: Partial<SignupDraft> = {};
                  if (patch.email !== undefined) draftPatch.email = patch.email;
                  if (patch.displayName !== undefined) draftPatch.displayName = patch.displayName;
                  if (Object.keys(draftPatch).length > 0) patchDraft(draftPatch);
                }}
                onSubmit={submitOwnerStep}
                onBack={() => goToStep(1)}
              />
            ) : null}

            {step === 3 && hasPassword ? (
              <DpaFerpaScreen
                teamName={draft.teamName}
                dpaVersion={dpaVersion}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onBack={() => goToStep(2)}
                onAccept={() => void createTeam()}
              />
            ) : null}
          </>
        ) : (
          <SignupSuccess result={result} />
        )}
      </main>
      <p className="auth__footnote">
        Already have a portal? <Link to={ROUTES.login}>Sign in</Link>
      </p>
    </div>
  );
}
